import React from "react";
import cx from "clsx";
import { navigate } from "gatsby";
import { setConfiguration } from "react-grid-system";
import { isBrowser, isTablet, isMobile } from "react-device-detect";

import Layout from "@/components/shared/layout";
import Navbar from "@/components/navbar";
import SEO from "@/components/seo";
import Dimmer from "@/components/shared/dimmer";
import Button from "@/components/shared/button";
import ImageButton from "@/components/shared/imageButton";
import VideoTour from "@/components/tour/videoTour";
import TourIntro from "@/components/tour/intro";
import TourInstructions from "@/components/tour/tourInstructions";
import TourProgress from "@/components/tour/progress";
import TourOutro from "@/components/tour/tourOutro";
import VideoStopPoints from "@/components/tour/videoStopPoints";
import VideoStopDetails from "@/components/tour/videoStopDetails";
import Section1 from "@/components/sections/section1";
import Section2 from "@/components/sections/section2";
import Section3 from "@/components/sections/section3";
import Section4 from "@/components/sections/section4";
import Section6 from "@/components/sections/section6";
import ThreeSixtyModal from "@/components/tour/threeSixtyModal";
import MobileTourBottom from "@/components/tour/mobileTourBottom";
import withModals from "@/components/shared/withModals";
import {
  lockBodyScrolling,
  restoreBodyScrolling,
  getStopIndex,
  getTourOffset,
  getProgressOffset,
  getTourOutroHeight,
  throttle,
} from "@/helpers";
import {
  PLAYBACK_CONST,
  SCROLL_HEIGHT,
  MIN_LOAD_TIME,
  SCROLL_THROTTLE,
  DETAIL_POINTS,
  SCREEN_CONFIG,
  DESKTOP_ANIMATION_TIME,
} from "@/constants";
import HeroImg from "@/components/images/home/hero";
import Home01 from "@/components/images/home/home-01";
import Home02 from "@/components/images/home/home-02";
import Home03 from "@/components/images/home/home-03";
import Home04 from "@/components/images/home/home-04";
import Home05 from "@/components/images/home/home-05";
import Home06 from "@/components/images/home/home-06";
import Home07 from "@/components/images/home/home-07";
import DesktopMap from "@/components/images/home/desktop-map";
import MobileMap from "@/components/images/home/mobile-map";
import Eighteen56Svg from "@/images/shared/eighteen-56.svg";
import SparkPlugBgSvg from "@/images/home/spark-plug.svg";
import SparkPlugSvg from "@/images/shared/spark-plug.svg";
import PatternImg from "@/images/shared/beam-pattern.png";
import LogoSvg from "@/images/home/logo.svg";
import PalmSvg from "@/images/home/palm.svg";

import globalStyles from "@/styles/global.module.scss";
import vars from "@/styles/_variables.scss";
import styles from "./index.module.scss";

// Grid system init
setConfiguration(SCREEN_CONFIG);

const OUTRO_LINKS = [
  { link: "/leasing", label: "Building Details" },
  { link: "/film", label: "Watch the Film" },
];

class IndexPage extends React.PureComponent {
  state = {
    hasMounted: false,
    panoEnabled: false,
    videoLoaded: false,
    tourReady: false,
    threeSixtyModalMounted: false,
    threeSixtyModalVisible: false,
    threeSixtyData: null,
    stopIndex: -1,
    scrollOffset: 0,
    currentFrame: 0,
    scrollPercent: 0,
    detailsMenuOpen: false,
    tempSectionsHeight: 0,
    outroHeight: 0,
    videoHeight: 0,
    pastTour: false,
  };

  sectionsRef = React.createRef();

  static getDerivedStateFromProps(nextProps, prevState) {
    const { isDesktop, windowHeight } = nextProps;
    const { scrollOffset, hasMounted } = prevState;

    // Scroll amount before video starts
    const tourOffset = getTourOffset(isDesktop);
    // Scroll amount before progress bar starts
    const progressOffset = getProgressOffset(isDesktop);
    // Amount to scroll during outro
    const outroHeight = getTourOutroHeight(windowHeight, isDesktop);
    // Current frame of video
    const currentFrame =
      Math.max(scrollOffset - tourOffset, 0) / PLAYBACK_CONST;
    // Tour height
    const tourHeight = tourOffset + SCROLL_HEIGHT;
    // Progress indicator
    const scrollPercent =
      (Math.max(scrollOffset - progressOffset, 0) /
        (tourHeight - windowHeight - tourOffset)) *
      100;
    // Current stop
    const stopIndex = getStopIndex(
      currentFrame,
      scrollOffset,
      tourHeight + outroHeight + windowHeight
    );
    const additionalHeight = isMobile ? 50 : 0;
    let videoHeight = isDesktop
      ? windowHeight + additionalHeight
      : windowHeight - parseInt(vars.tourBottomHeight) + additionalHeight;
    if (!hasMounted) videoHeight = 0;

    const pastTour =
      scrollOffset > tourOffset + SCROLL_HEIGHT + outroHeight + windowHeight;

    return {
      ...prevState,
      currentFrame,
      scrollPercent,
      stopIndex,
      outroHeight,
      videoHeight,
      pastTour,
    };
  }

  componentDidMount() {
    // Wait for video load
    lockBodyScrolling();

    this.setState({
      hasMounted: true,
      startTime: new Date().getTime(),
    });
    // Jump to top
    setTimeout(this.handleJumpToTop, 10);
    setTimeout(this.handleJumpToTop, 100);
    setTimeout(this.handleJumpToTop, 330);

    // Only used when hot-reloading
    if (this.state.videoLoaded) {
      setTimeout(() => this.setState({ tourReady: true }), MIN_LOAD_TIME);
    }

    window.addEventListener("scroll", this.handleWindowScroll, {
      passive: true,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { isDesktop } = this.props;
    const { videoLoaded, startTime } = this.state;

    if (!prevState.videoLoaded && videoLoaded) {
      const endTime = new Date().getTime();
      const diff = endTime - startTime;
      const wait = Math.max(MIN_LOAD_TIME - diff, 0);
      // Mininum loading time
      setTimeout(() => {
        this.setState({ tourReady: true });
      }, wait);
      // Intro Animation time
      setTimeout(
        () => {
          restoreBodyScrolling();
        },
        isDesktop ? wait + DESKTOP_ANIMATION_TIME : wait
      );

      // Store height of rendered sections
      if (this.sectionsRef && this.sectionsRef.current) {
        const tempSectionsHeight = this.sectionsRef.current.clientHeight;

        this.setState({ tempSectionsHeight });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleWindowScroll);

    restoreBodyScrolling();
  }

  handleJumpToTop = () => {
    window.scrollTo(0, 0);
    this.handleWindowScroll();
  };

  handleWindowScroll = throttle(() => {
    if (this.state.tourReady) {
      this.setState({ scrollOffset: window.pageYOffset });
    }
  }, SCROLL_THROTTLE);

  handleLoadVideo = () => {
    console.log("loaded video");
    this.setState({ videoLoaded: true });
    // restoreBodyScrolling();
  };

  handleStopClick = (point) => {
    // console.log("point", point);
    if (point.threeSixty) {
      // Disable scroll
      lockBodyScrolling();
      // Open modal
      this.setState({
        threeSixtyModalMounted: true,
        threeSixtyModalVisible: true,
        threeSixtyData: point,
      });
    }
  };

  handleDetailsMenuClick = () => {
    this.setState({ detailsMenuOpen: !this.state.detailsMenuOpen });
  };

  handleCloseThreeSixty = () => {
    // Enable scroll
    restoreBodyScrolling();
    // Start close animation
    this.setState({
      threeSixtyModalVisible: false,
    });
  };

  handleCloseThreeSixtyComplete = () => {
    // Complete animation
    this.setState({
      threeSixtyModalMounted: false,
      threeSixtyData: null,
    });
  };

  handleClickLeasing = () => navigate("/leasing");

  handleClickLifestyle = () => navigate("/lifestyle");

  handleClickNeighborhood = () => navigate("/location");

  handleOpenGalleryImage = (index) => () => this.props.handleOpenGallery(index);

  renderThreeSixtyModal = () => {
    const { isDesktop } = this.props;
    const {
      threeSixtyData,
      threeSixtyModalMounted,
      threeSixtyModalVisible,
    } = this.state;

    if (!threeSixtyData) return null;

    const { panoIndex, bldg, label } = threeSixtyData;

    return threeSixtyModalMounted ? (
      <Dimmer
        visible={threeSixtyModalVisible}
        onClose={this.handleCloseThreeSixtyComplete}
        className={styles.threeSixtyDimmer}
      >
        <ThreeSixtyModal
          bldg={bldg}
          label={label}
          visible={threeSixtyModalVisible}
          panoIndex={panoIndex}
          isDesktop={isDesktop}
          onClose={this.handleCloseThreeSixty}
        />
      </Dimmer>
    ) : null;
  };

  renderSections = () => {
    const { screenClass, isDesktop, windowWidth } = this.props;
    const { outroHeight } = this.state;

    const disableParallax = false; // isTablet || isMobile || isMobileSafari;

    return (
      <div
        ref={this.sectionsRef}
        className={styles.sectionBg}
        style={{
          marginTop: SCROLL_HEIGHT + outroHeight,
        }}
      >
        <Section1
          type="right"
          screenClass={screenClass}
          isDesktop={isDesktop}
          disableParallax={disableParallax}
          title={
            !isDesktop || ["md", "lg"].includes(screenClass)
              ? ["A CREATIVE", "SPARK", "FOR TODAY'S", "MAKERS", "& PIONEERS"]
              : ["A CREATIVE", "SPARK", "FOR TODAY'S MAKERS", "& PIONEERS"]
          }
          paragraph={
            <p>
              Introducing ELCO Yards, Silicon Valley’s most dynamic work/life
              district in Downtown Redwood City. Featuring 592,000 SF of brand
              new, Class A office space, ELCO Yards is an inspired mix of great
              urban work and living places designed for the future of Being and
              Doing. With food, drink, and wellness amenities spread throughout
              four architecturally stunning buildings, ELCO Yards will provide
              the environment for enterprising pioneers to be constantly
              ignited.
            </p>
          }
          button={
            <Button onClick={this.handleClickLeasing}>Leasing & More</Button>
          }
          image={
            <ImageButton onClick={this.handleOpenGalleryImage(3)}>
              <Home01 alt="Home-01" />
            </ImageButton>
          }
          caption={
            "ELCO Yards is comprised of four commercial buildings with approximately 592,000 SF, and two residential communities featuring 500 homes in Downtown Redwood City."
          }
          bgTop={
            <SparkPlugBgSvg alt="Spark plug" className={styles.sparkPlugSvg} />
          }
          bgTopClassName={styles.sparkPlugContainer}
          bgBottom={
            isDesktop && <img src={PatternImg} alt="Background pattern" />
          }
          // containerClassName={styles.section1}
        />

        <Section2
          type="right"
          transparent
          imageBorder
          screenClass={screenClass}
          isDesktop={isDesktop}
          disableParallax={disableParallax}
          title={[
            "",
            "REDWOOD CITY:",
            "A STORIED PAST",
            "WITH A HISTORY",
            "OF INNOVATION",
          ]}
          paragraph={
            <p>
              ELCO Yards rises up in the South Main district of Downtown Redwood
              City - a storied working-class neighborhood with a long history of
              hard work and innovation. Now the premier innovation hub on the
              Peninsula, ELCO Yards is perfectly situated for convenient access
              to anywhere in the Bay Area.
            </p>
          }
          image={<Home02 alt="Home-02" />}
          bgImg={<Eighteen56Svg alt="1856" />}
          bgImgClassName={styles.eighteenSvg}
          containerClassName={styles.section2}
        />

        <Section3
          title={["BIG CITY", "PERKS +", "SMALL TOWN", "VIBE"]}
          paragraph={
            <p>
              Just steps from a thriving and walkable downtown, Redwood City has
              it all - from curated retail to diverse dining and entertainment
              there’s something for everyone. At ELCO Yards you’ll have a
              variety of shops and restaurants just at your doorstop. Take a
              look at everything ELCO has to offer.
            </p>
          }
          images={[
            <Home03 key="home-03" alt="Home-03" />,
            <Home04 key="home-04" alt="Home-04" />,
            <Home05 key="home-05" alt="Home-05" />,
          ]}
          windowWidth={windowWidth}
          screenClass={screenClass}
          isDesktop={isDesktop}
          button={
            <Button onClick={this.handleClickNeighborhood}>
              See the Neighborhood
            </Button>
          }
          // containerClassName={styles.section3}
        />

        <div className={styles.map}>
          {["xs", "sm"].includes(screenClass) ? (
            <MobileMap alt="Map" />
          ) : (
            <DesktopMap alt="Map" />
          )}
          <div
            className={cx(
              globalStyles.caption,
              globalStyles.center,
              globalStyles.contentPaddingH,
              styles.mapCaption
            )}
          >
            ELCO Yards sits between El Camino and the Caltrain line, and is a
            short walk to all of downtown Redwood City and the Caltrain. For
            commuters, ELCO provides instant access to CA 84 and Hwy 101.
          </div>
        </div>

        <Section6
          icon={
            <SparkPlugSvg className={globalStyles.image} alt="Spark plug" />
          }
          bgImg={<LogoSvg className={styles.logoImg} alt="Logo" />}
          bgImgY={
            ["xs", "sm"].includes(screenClass)
              ? ["-40px", "20px"]
              : ["150px", "280px"]
          }
          bgImgClassName={cx(
            styles.logoImgContainer,
            isDesktop && styles.isDesktop,
            isTablet && styles.isTablet
          )}
          eyeBrowText={
            !isDesktop
              ? ["A mixed-use district", "with a harmonic balance of"]
              : "A mixed-use district with a harmonic balance of"
          }
          headerText={["DOING & BEING"]}
          headerLarge
          screenClass={screenClass}
          isDesktop={isDesktop}
          disableParallax={disableParallax}
          containerClassName={cx(styles.sectionBg, styles.section5)}
        />

        <Section4
          imageBorder
          image={
            <ImageButton onClick={this.handleOpenGalleryImage(1)}>
              <div
                className={cx(
                  screenClass === "xs" && globalStyles.fullWidthImage
                )}
              >
                <Home06 alt="Home-06" />
              </div>
            </ImageButton>
          }
          caption="ELCO Yards will feature 2+ acres of onsite open space. The reimagining of the Perry Feed & Fuel Building will create a new home for a dynamic restaurant and drink spot replete with hanging bistro lights, an outdoor stage, and a gazebo."
          screenClass={screenClass}
          isDesktop={isDesktop}
          disableParallax={disableParallax}
          bgImgClassName={styles.section6BgImg}
          innerClassName={styles.section6Inner}
          borderClassName={styles.section6Border}
          containerClassName={cx(styles.sectionBg, styles.section6)}
        />

        <Section2
          type="right"
          transparent
          imageBorder
          screenClass={screenClass}
          isDesktop={isDesktop}
          disableParallax={disableParallax}
          title={
            isDesktop
              ? ["", "THE PERFECT", "BLEND", "OF CULTURE", "AND COMMERCE"]
              : ["", "THE PERFECT", "BLEND OF", "CULTURE &", "COMMERCE"]
          }
          paragraph={
            <p>
              Featuring over 500 new residential homes, ELCO Yards will also be
              a premier living destination for the next generation of the
              Silicon Valley workforce. With over 28,800 SF of new retail, as
              well as the renovation of the historic Feed & Fuel landmark, ELCO
              Yards will be one of Redwood City’s most exciting new destinations
              to play and unwind.
            </p>
          }
          image={<Home07 alt="Home-07" />}
          bgImg={<PalmSvg alt="Palm" className={styles.palmSvg} />}
          bgImgY={
            screenClass === "xs"
              ? ["0px", "300px"]
              : screenClass === "md"
              ? ["-240px", "0px"]
              : ["-340px", "100px"]
          }
          bgImgClassName={cx(
            styles.section7BgImg,
            isBrowser && styles.isDesktop
          )}
          containerClassName={cx(styles.sectionBg, styles.section7)}
          button={
            <Button onClick={this.handleClickLifestyle}>
              See the Lifestyle
            </Button>
          }
        />
      </div>
    );
  };

  render() {
    const {
      location,
      screenClass,
      isDesktop,
      userIsTabbing,
      handleOpenGallery,
      handleOpenFilm,
      windowWidth,
      windowHeight,
      safariHeight,
    } = this.props;
    const {
      stopIndex,
      scrollPercent,
      hasMounted,
      tourReady,
      tempSectionsHeight,
      outroHeight,
      pastTour,
      videoHeight,
      threeSixtyModalVisible,
    } = this.state;

    const stopPointsVisible = stopIndex > 2 && stopIndex < 10 ? true : false;
    const detailsVisible = !!DETAIL_POINTS[stopIndex];
    const hideSections = tempSectionsHeight > 0 && stopIndex < 10;

    return (
      <Layout
        userIsTabbing={userIsTabbing}
        onGalleryClick={handleOpenGallery}
        onFilmClick={handleOpenFilm}
        windowHeight={windowHeight}
        screenClass={screenClass}
        isDesktop={isDesktop}
        hideLogo={threeSixtyModalVisible}
        className={cx(styles.layout, isDesktop && globalStyles.desktopLayout)}
      >
        <SEO
          title="New Innovative Offices in Redwood City"
          description="Introducing ELCO Yards, Silicon Valley’s most dynamic work/life district in Downtown Redwood City. 
          Featuring approximately 590,000 SF of dynamic, brand new Class A office space including large floor plates, 100% outdoor 
          air circulation, and ample terraces on every building."
        />

        <Navbar
          location={location}
          fromNavbar={location.state && location.state.fromNavbar}
          onGalleryClick={handleOpenGallery}
          onFilmClick={handleOpenFilm}
          tourReady={tourReady}
          screenClass={screenClass}
          isDesktop={isDesktop}
        />

        <TourIntro
          stopIndex={stopIndex}
          screenClass={screenClass}
          tourReady={tourReady}
          isDesktop={isDesktop}
          windowHeight={windowHeight}
        />

        {isDesktop ? (
          <TourInstructions
            tourReady={tourReady}
            isDesktop={isDesktop}
            windowHeight={windowHeight}
          />
        ) : null}

        {!isDesktop && stopIndex < 11 ? (
          <MobileTourBottom
            percent={scrollPercent}
            stopIndex={stopIndex}
            screenClass={screenClass}
            isDesktop={false}
            windowHeight={windowHeight}
            safariHeight={safariHeight}
            tourReady={tourReady}
          />
        ) : null}

        {!pastTour ? (
          <VideoStopPoints
            stopIndex={stopIndex}
            isDesktop={isDesktop}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            onMenuToggle={this.handleDetailsMenuClick}
            onStopClick={this.handleStopClick}
            visible={stopPointsVisible}
          />
        ) : null}

        {isDesktop && !pastTour ? (
          <VideoStopDetails
            stopIndex={stopIndex}
            isDesktop={isDesktop}
            visible={detailsVisible}
          />
        ) : null}

        {!pastTour ? (
          <TourOutro
            visible={stopIndex >= 10}
            isCovered={stopIndex > 10}
            isDesktop={isDesktop}
            windowHeight={windowHeight}
            safariHeight={safariHeight}
            onFilmClick={handleOpenFilm}
            links={OUTRO_LINKS}
          />
        ) : (
          <div className={styles.fullHeightBackground} />
        )}

        {hasMounted ? (
          <VideoTour
            isDesktop={isDesktop}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            safariHeight={safariHeight}
            onLoadVideo={this.handleLoadVideo}
            videoHeight={videoHeight}
            hideVideo={pastTour}
          />
        ) : null}

        {isDesktop && stopIndex < 11 ? (
          <TourProgress
            percent={scrollPercent}
            stopIndex={stopIndex}
            isDesktop={isDesktop}
          />
        ) : null}

        {hideSections ? (
          <div
            style={{
              height: SCROLL_HEIGHT + outroHeight + tempSectionsHeight,
            }}
          />
        ) : (
          this.renderSections()
        )}

        {this.renderThreeSixtyModal()}

        <div
          className={cx(
            globalStyles.image,
            styles.loadingBackground,
            tourReady && styles.loaded
          )}
          style={{ maxHeight: videoHeight }}
        >
          <HeroImg className={globalStyles.image} alt="Elco Yards" />
        </div>
      </Layout>
    );
  }
}

export default withModals(IndexPage);
